<template>
  <Loader
    v-if="isLoading"
    style="z-index:1102"
  />
  <div class="grid">
    <div class="col-12 flex flex-column">
      <div class="flex flex-column">
        <!-- Toggle UTM active -->
        <div class="field">
          <label for="utmActive">
            {{ t('storeParameters.services.utmServiceParameter.fields.active') }}
          </label>
          <div class="flex align-items-center gap-2 mt-1">
            <InputSwitch
              id="utmActive"
              v-model="utmActive"
            />
            <span class="text-sm" style="color: var(--text-color-secondary);">
              {{ utmActive ? t('storeParameters.services.utmServiceParameter.activeLabel') : t('storeParameters.services.utmServiceParameter.inactiveLabel') }}
            </span>
          </div>
        </div>

        <div
          v-if="utmActive"
          class="flex flex-column gap-3"
        >
          <!-- UTM Fields -->
          <div class="field">
            <label for="utmSource">
              utm_source
            </label>
            <div class="p-inputgroup">
              <InputText
                id="utmSource"
                v-model="field.shopRequiredDataUtm_source"
                placeholder="shopimind"
                class="w-full"
              />
              <span
                class="p-inputgroup-addon cursor-pointer utm-var-btn"
                :title="t('automatedScenarios.actions.sending_channel.utm_config.variablePicker.insertVariable')"
                @click="openVariablePicker($event, 'shopRequiredDataUtm_source')"
              >
                <i class="fa-solid fa-code" />
              </span>
            </div>
            <small class="text-xs" style="color: var(--text-color-secondary);">
              {{ t('storeParameters.services.utmServiceParameter.hints.source') }}
            </small>
          </div>

          <div class="field">
            <label for="utmMedium">
              utm_medium
            </label>
            <div class="p-inputgroup">
              <InputText
                id="utmMedium"
                v-model="field.shopRequiredDataUtm_medium"
                placeholder="{var=channel}"
                class="w-full"
              />
              <span
                class="p-inputgroup-addon cursor-pointer utm-var-btn"
                :title="t('automatedScenarios.actions.sending_channel.utm_config.variablePicker.insertVariable')"
                @click="openVariablePicker($event, 'shopRequiredDataUtm_medium')"
              >
                <i class="fa-solid fa-code" />
              </span>
            </div>
            <small class="text-xs" style="color: var(--text-color-secondary);">
              {{ t('storeParameters.services.utmServiceParameter.hints.medium', { variable: '{var=channel}' }) }}
            </small>
          </div>

          <div class="field">
            <label for="utmCampaign">
              utm_campaign
            </label>
            <div class="p-inputgroup">
              <InputText
                id="utmCampaign"
                v-model="field.shopRequiredDataUtm_campaign"
                placeholder="{var=campaign_name}"
                class="w-full"
              />
              <span
                class="p-inputgroup-addon cursor-pointer utm-var-btn"
                :title="t('automatedScenarios.actions.sending_channel.utm_config.variablePicker.insertVariable')"
                @click="openVariablePicker($event, 'shopRequiredDataUtm_campaign')"
              >
                <i class="fa-solid fa-code" />
              </span>
            </div>
            <small class="text-xs" style="color: var(--text-color-secondary);">
              {{ t('storeParameters.services.utmServiceParameter.hints.campaign', { variable: '{var=campaign_name}' }) }}
            </small>
          </div>

          <div class="field">
            <label for="utmId">
              utm_id
            </label>
            <div class="p-inputgroup">
              <InputText
                id="utmId"
                v-model="field.shopRequiredDataUtm_id"
                :placeholder="t('storeParameters.services.utmServiceParameter.emptyPlaceholder')"
                class="w-full"
              />
              <span
                class="p-inputgroup-addon cursor-pointer utm-var-btn"
                :title="t('automatedScenarios.actions.sending_channel.utm_config.variablePicker.insertVariable')"
                @click="openVariablePicker($event, 'shopRequiredDataUtm_id')"
              >
                <i class="fa-solid fa-code" />
              </span>
            </div>
            <small class="text-xs" style="color: var(--text-color-secondary);">
              {{ t('storeParameters.services.utmServiceParameter.hints.id') }}
            </small>
          </div>

          <div class="field">
            <label for="utmContent">
              utm_content
            </label>
            <div class="p-inputgroup">
              <InputText
                id="utmContent"
                v-model="field.shopRequiredDataUtm_content"
                :placeholder="t('storeParameters.services.utmServiceParameter.emptyPlaceholder')"
                class="w-full"
              />
              <span
                class="p-inputgroup-addon cursor-pointer utm-var-btn"
                :title="t('automatedScenarios.actions.sending_channel.utm_config.variablePicker.insertVariable')"
                @click="openVariablePicker($event, 'shopRequiredDataUtm_content')"
              >
                <i class="fa-solid fa-code" />
              </span>
            </div>
            <small class="text-xs" style="color: var(--text-color-secondary);">
              {{ t('storeParameters.services.utmServiceParameter.hints.content') }}
            </small>
          </div>

          <div class="field">
            <label for="utmTerm">
              utm_term
            </label>
            <div class="p-inputgroup">
              <InputText
                id="utmTerm"
                v-model="field.shopRequiredDataUtm_term"
                :placeholder="t('storeParameters.services.utmServiceParameter.emptyPlaceholder')"
                class="w-full"
              />
              <span
                class="p-inputgroup-addon cursor-pointer utm-var-btn"
                :title="t('automatedScenarios.actions.sending_channel.utm_config.variablePicker.insertVariable')"
                @click="openVariablePicker($event, 'shopRequiredDataUtm_term')"
              >
                <i class="fa-solid fa-code" />
              </span>
            </div>
            <small class="text-xs" style="color: var(--text-color-secondary);">
              {{ t('storeParameters.services.utmServiceParameter.hints.term') }}
            </small>
          </div>

          <!-- Custom UTM Parameters -->
          <div class="field">
            <label class="font-semibold block mb-2">
              {{ t('storeParameters.services.utmServiceParameter.customParams.title') }}
            </label>
            <div
              v-for="(param, index) in customParams"
              :key="index"
              class="flex align-items-center gap-2 mb-2"
            >
              <InputText
                v-model="param.key"
                :placeholder="t('storeParameters.services.utmServiceParameter.customParams.keyPlaceholder')"
                class="w-15rem flex-shrink-0"
              />
              <div class="p-inputgroup flex-grow-1">
                <InputText
                  v-model="param.value"
                  :placeholder="t('storeParameters.services.utmServiceParameter.customParams.valuePlaceholder')"
                  class="w-full"
                />
                <span
                  class="p-inputgroup-addon cursor-pointer utm-var-btn"
                  :title="t('automatedScenarios.actions.sending_channel.utm_config.variablePicker.insertVariable')"
                  @click="openVariablePickerCustom($event, index)"
                >
                  <i class="fa-solid fa-code" />
                </span>
              </div>
              <Button
                icon="fa-solid fa-trash-can"
                class="p-button-text p-button-danger p-button-sm"
                @click="removeCustomParam(index)"
              />
            </div>
            <Button
              :label="t('storeParameters.services.utmServiceParameter.customParams.addButton')"
              icon="fa-solid fa-plus"
              class="p-button-outlined p-button-sm mt-1"
              @click="addCustomParam"
            />
          </div>

          <!-- Variable Picker Overlay (shared by all fields) -->
          <OverlayPanel
            ref="variablePickerRef"
            append-to="body"
          >
            <div class="flex flex-column" style="width: 320px;">
              <InputText
                v-model="variableFilter"
                :placeholder="t('automatedScenarios.actions.sending_channel.utm_config.variablePicker.searchPlaceholder')"
                class="w-full mb-2 p-inputtext-sm"
              />
              <div class="utm-var-list">
                <div
                  v-for="opt in filteredOptions"
                  :key="opt.value"
                  class="utm-var-option"
                  @click="selectVariable(opt)"
                >
                  <div class="flex align-items-center gap-2">
                    <i
                      v-if="opt.isCustom"
                      class="fa-solid fa-puzzle-piece custom-icon"
                    />
                    <div class="flex flex-column">
                      <span class="text-sm">{{ opt.displayLabel }}</span>
                      <span class="text-xs" style="color: var(--text-color-secondary);">{{ opt.value }}</span>
                    </div>
                  </div>
                  <span
                    v-if="opt.isCustom"
                    class="custom-badge-inline"
                  >
                    CUSTOM
                  </span>
                </div>
                <div
                  v-if="filteredOptions.length === 0"
                  class="px-3 py-2 text-sm text-color-secondary"
                >
                  {{ t('automatedScenarios.actions.sending_channel.utm_config.variablePicker.noResults') }}
                </div>
              </div>
            </div>
          </OverlayPanel>

          <!-- URL Preview -->
          <div class="field">
            <label class="font-semibold">
              {{ t('storeParameters.services.utmServiceParameter.preview') }}
            </label>
            <div
              class="p-2 border-1 border-round surface-ground text-sm"
              style="word-break: break-all; font-family: monospace;"
              v-html="previewUrl"
            />
          </div>
        </div>

        <SpmButton
          v-if="!globalSave"
          :label="t('save')"
          :loading="saveLoading"
          class="p-button p-button-primary mt-3"
          @click="handleSave"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  defineExpose,
  ref,
  reactive,
  computed,
  onMounted,
} from 'vue';

import InputText from 'primevue/inputtext';
import InputSwitch from 'primevue/inputswitch';
import OverlayPanel from 'primevue/overlaypanel';
import Button from 'primevue/button';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import Loader from '@/components/layout/Loader.vue';

import { showToastError, showToastSuccess } from '@/helpers';
import {
  saveShopParamsOnRedis,
  fetchShopConfigurationsNest,
  saveShopConfigurationsNest,
} from '@/composables/shop/ShopsConfiguration';
import { useUtmDropdownOptions, DropdownOption } from '@/composables/flows/useUtmDropdownOptions';
import { UserState } from '@/composables/User';

import { useI18n } from 'vue-i18n';

const UTM_DEFAULTS: Record<string, string> = {
  shopRequiredDataUtm_source: 'shopimind',
  shopRequiredDataUtm_medium: '{var=channel}',
  shopRequiredDataUtm_campaign: '{var=campaign_name}',
  shopRequiredDataUtm_id: '',
  shopRequiredDataUtm_content: '',
  shopRequiredDataUtm_term: '',
};

export default defineComponent({
  name: 'UtmServiceParameter',

  components: {
    InputText,
    InputSwitch,
    OverlayPanel,
    Button,
    SpmButton,
    Loader,
  },

  props: {
    shopId: {
      type: Number,
      required: true,
    },

    globalSave: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['close'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const { dropdownOptions, loadCustomData } = useUtmDropdownOptions(t);

    const isLoading = ref(false);
    const saveLoading = ref(false);
    const utmActive = ref(true);

    const field = reactive<Record<string, string>>({
      shopRequiredDataUtm_source: '',
      shopRequiredDataUtm_medium: '',
      shopRequiredDataUtm_campaign: '',
      shopRequiredDataUtm_id: '',
      shopRequiredDataUtm_content: '',
      shopRequiredDataUtm_term: '',
    });

    // Custom UTM params
    const customParams = ref<Array<{ key: string; value: string }>>([]);

    const addCustomParam = () => {
      customParams.value.push({ key: '', value: '' });
    };

    const removeCustomParam = (index: number) => {
      customParams.value.splice(index, 1);
    };

    // Variable picker state
    const variablePickerRef = ref();
    const variableFilter = ref('');
    const activeField = ref('');
    const activeCustomIndex = ref(-1);

    const filteredOptions = computed(() => {
      if (!variableFilter.value) return dropdownOptions.value;
      const q = variableFilter.value.toLowerCase();
      return dropdownOptions.value.filter(
        (opt) => opt.displayLabel.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q),
      );
    });

    const openVariablePicker = (event: Event, fieldKey: string) => {
      activeField.value = fieldKey;
      activeCustomIndex.value = -1;
      variableFilter.value = '';
      variablePickerRef.value.toggle(event);
    };

    const openVariablePickerCustom = (event: Event, index: number) => {
      activeField.value = '';
      activeCustomIndex.value = index;
      variableFilter.value = '';
      variablePickerRef.value.toggle(event);
    };

    const selectVariable = (opt: DropdownOption) => {
      if (activeCustomIndex.value >= 0) {
        customParams.value[activeCustomIndex.value].value = opt.value;
      } else {
        field[activeField.value] = opt.value;
      }
      variablePickerRef.value.hide();
    };

    const escapeHtml = (str: string) => str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const previewUrl = computed(() => {
      const shopUrl = UserState.activeShop?.url || 'https://example.com';
      const baseUrl = shopUrl.startsWith('http') ? shopUrl : `https://${shopUrl}`;
      const source = escapeHtml(field.shopRequiredDataUtm_source || 'shopimind');
      const medium = escapeHtml(field.shopRequiredDataUtm_medium || '{var=channel}');
      const campaign = escapeHtml(field.shopRequiredDataUtm_campaign || '{var=campaign_name}');
      let url = `${escapeHtml(baseUrl)}?utm_source=<strong>${source}</strong>&amp;utm_medium=<strong>${medium}</strong>&amp;utm_campaign=<strong>${campaign}</strong>`;
      if (field.shopRequiredDataUtm_id) {
        url += `&amp;utm_id=<strong>${escapeHtml(field.shopRequiredDataUtm_id)}</strong>`;
      }
      if (field.shopRequiredDataUtm_content) {
        url += `&amp;utm_content=<strong>${escapeHtml(field.shopRequiredDataUtm_content)}</strong>`;
      }
      if (field.shopRequiredDataUtm_term) {
        url += `&amp;utm_term=<strong>${escapeHtml(field.shopRequiredDataUtm_term)}</strong>`;
      }
      for (const param of customParams.value) {
        if (param.key && param.value) {
          url += `&amp;${escapeHtml(param.key)}=<strong>${escapeHtml(param.value)}</strong>`;
        }
      }
      return url;
    });

    const handleSave = async () => {
      const configurationsToSave: Array<{ key: string; value: string; lang: string | null }> = [
        { key: 'shopRequiredDataUtm_activeConfig', value: utmActive.value ? '1' : '0', lang: null },
      ];

      Object.entries(field).forEach(([key, value]) => {
        configurationsToSave.push({
          key,
          value: value ?? '',
          lang: null,
        });
      });

      // Save custom params as JSON
      const validCustomParams = customParams.value.filter((p) => p.key);
      configurationsToSave.push({
        key: 'shopRequiredDataUtm_customParams',
        value: JSON.stringify(validCustomParams),
        lang: null,
      });

      if (props.globalSave) {
        return {
          err: null,
          configs: configurationsToSave.map((config) => ({
            key: config.key,
            value: config.value,
            lang: config.lang || '',
          })),
        };
      }

      saveLoading.value = true;

      try {
        const result = await saveShopConfigurationsNest(props.shopId, configurationsToSave);
        if (result) {
          await saveShopParamsOnRedis(props.shopId);
          showToastSuccess('savedSuccessful');
        } else {
          showToastError('GENERIC_ERROR');
        }
      } catch (err) {
        showToastError('GENERIC_ERROR');
      } finally {
        saveLoading.value = false;
      }
    };

    defineExpose({
      handleSave,
    });

    onMounted(async () => {
      try {
        isLoading.value = true;

        const [data] = await Promise.all([
          fetchShopConfigurationsNest(props.shopId, [
            'shopRequiredDataUtm_activeConfig',
            ...Object.keys(field),
            'shopRequiredDataUtm_customParams',
          ]),
          loadCustomData(),
        ]);

        if (data) {
          if ('shopRequiredDataUtm_activeConfig' in data) {
            utmActive.value = data.shopRequiredDataUtm_activeConfig !== '0';
          }

          Object.keys(field).forEach((key) => {
            if (key in data && data[key] !== null && data[key] !== undefined) {
              field[key] = data[key];
            }
          });

          // Load custom params
          if (data.shopRequiredDataUtm_customParams) {
            try {
              const parsed = JSON.parse(data.shopRequiredDataUtm_customParams);
              if (Array.isArray(parsed)) {
                customParams.value = parsed;
              }
            } catch {
              // Invalid JSON, ignore
            }
          }
        }

        // Apply defaults for fields that are still empty
        Object.keys(field).forEach((key) => {
          if (!field[key] && UTM_DEFAULTS[key]) {
            field[key] = UTM_DEFAULTS[key];
          }
        });
      } catch (err) {
        showToastError('GENERIC_ERROR');
      } finally {
        isLoading.value = false;
      }
    });

    return {
      t,
      isLoading,
      saveLoading,
      utmActive,
      field,
      customParams,
      addCustomParam,
      removeCustomParam,
      previewUrl,
      handleSave,
      dropdownOptions,
      variablePickerRef,
      variableFilter,
      filteredOptions,
      openVariablePicker,
      openVariablePickerCustom,
      selectVariable,
    };
  },
});
</script>

<style scoped>
.utm-var-btn {
  transition: background-color 0.2s;
}

.utm-var-btn:hover {
  background: var(--primary-color);
  color: var(--primary-color-text);
}

.utm-var-list {
  max-height: 280px;
  overflow-y: auto;
}

.utm-var-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  border-radius: var(--border-radius);
  transition: background-color 0.15s;
}

.utm-var-option:hover {
  background: var(--surface-hover);
}

.custom-icon {
  color: #8b5cf6;
  font-size: 0.85rem;
}

.custom-badge-inline {
  background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.15rem 0.4rem;
  border-radius: 0.25rem;
  text-transform: uppercase;
  white-space: nowrap;
}
</style>
